import {
  comboboxSelectors,
  MentionCombobox,
  type TComboboxItemWithData,
} from '@udecode/plate'
import type {
  AutocompleteData,
  AutocompleteItem,
} from '~publish/legacy/editor/plugins/autocomplete'
import { FacebookMentionInputElement } from '~publish/legacy/editor/plugins/facebook-mention/nodes'
import { LinkedInAnnotationInputElement } from '~publish/legacy/editor/plugins/linkedin-annotations/nodes'
import { selectAndInsertSuggestion } from '../../../../../editor/plugins/autocomplete/transforms/selectAndInsertSuggestion'
import { AutocompleteSuggestion } from '../../../../../editor/plugins/autocomplete/ui/AutocompleteSuggestion'
import styles from './MentionComboboxes.module.css'

type MentionComboboxesProps = {
  portalElement?: HTMLElement
}

const comboboxStyles = {
  root: styles.root,
  item: styles.item,
  highlightedItem: styles.highlightedItem,
}

function Info(): JSX.Element | null {
  const items = comboboxSelectors.items()
  const filteredItems = comboboxSelectors.filteredItems()
  const text = (comboboxSelectors.text() || '').replace(/^@/, '')

  if (text.length === 0 && filteredItems.length > 0) {
    return (
      <div className={styles.recents}>
        <span className={styles.infoText}>Recents</span>
      </div>
    )
  }

  if (text.length <= 2 && text.length >= 1) {
    return (
      <div className={styles.info}>
        <span className={styles.infoText}>
          Please type at least 3 characters
        </span>
      </div>
    )
  }

  if (text.length >= 3 && items.length === 1) {
    const key = items[0].key

    if (key === 'empty') {
      return (
        <div className={styles.info}>
          <span className={styles.infoText}>
            No LinkedIn connections matched
          </span>
        </div>
      )
    }

    if (key === 'not-found') {
      return (
        <div className={styles.info}>
          <span className={styles.infoText}>
            We couldn&apos;t find a LinkedIn Page at this URL
          </span>
        </div>
      )
    }
  }

  return null
}

// override internal filter as we're already filtering
const handleFilter =
  () =>
  (item: TComboboxItemWithData<AutocompleteData>): boolean =>
    !!item && !['empty', 'loading', 'not-found'].includes(item.key)

const handleOnRenderItem = ({
  item,
}: {
  item: AutocompleteItem
}): JSX.Element => <AutocompleteSuggestion mention={item} />

export const MentionComboboxes = ({
  portalElement,
}: MentionComboboxesProps): JSX.Element => {
  return (
    <>
      <MentionCombobox<AutocompleteData>
        pluginKey={FacebookMentionInputElement.type}
        portalElement={portalElement}
        onRenderItem={handleOnRenderItem}
        onSelectItem={selectAndInsertSuggestion<FacebookMentionInputElement>(
          FacebookMentionInputElement,
          true,
        )}
        filter={handleFilter}
        classNames={comboboxStyles}
      />

      <MentionCombobox<AutocompleteData>
        pluginKey={LinkedInAnnotationInputElement.type}
        portalElement={portalElement}
        onRenderItem={handleOnRenderItem}
        onSelectItem={selectAndInsertSuggestion<LinkedInAnnotationInputElement>(
          LinkedInAnnotationInputElement,
          true,
        )}
        filter={handleFilter}
        classNames={comboboxStyles}
        component={Info}
      />
    </>
  )
}
